import { IafItemSvc } from '../../services/platformApi';
import type { PlatformContext } from '../../types/config';
// Local JSON seed (optional)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Vite allows importing JSON
import bimEquipment from '../../scripts/bim_equipment.json';

export const CATALOG_USER_TYPE = 'equipment_types_catalog';

export interface CatalogCollection {
  _id: string;
  _userItemId?: string;
  _name?: string;
  _userType?: string;
  _namespaces?: string[];
}

export interface CatalogEntry {
  _id?: string;
  category: string;
  typeName: string;
  typeCode: string;
}

export function generateTypeCode(typeName: string): string {
  if (!typeName) return '';
  return typeName
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export async function getCatalogCollection(ctx: PlatformContext): Promise<CatalogCollection | null> {
  try {
    const res = await IafItemSvc.getNamedUserItems(
      { query: { _userType: CATALOG_USER_TYPE } },
      ctx,
      { _pageSize: 1 }
    );
    return res._list?.[0] || null;
  } catch (e) {
    console.warn('getCatalogCollection failed', e);
    return null;
  }
}

export async function createCatalogCollection(ctx: PlatformContext, namespaces: string[], name = 'Equipment Types Catalog'): Promise<CatalogCollection | null> {
  try {
    // Using low-level REST similar to existing patterns in the app
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const baseUri = (await import('../../services/platformApi')).IafFetch.CONFIG.itemServiceOrigin;
    // Use ONLY the first namespace - project's primary namespace
    const ns = namespaces?.[0];
    // _shortName must be 1..22 chars. Use a compact, stable short name.
    const shortName = 'equip_types_cat';
    const payload = [{
      _itemClass: 'NamedUserCollection',
      _name: name,
      _shortName: shortName,
      _description: 'Managed catalog of equipment categories and types',
      _userType: CATALOG_USER_TYPE,
      _namespaces: ns ? [ns] : [], // Ensure only single namespace array
    }];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const token = (ctx as any).authToken;
    const resp = await fetch(`${baseUri}/itemsvc/api/v1/nameduseritems?nsfilter=${ns}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const body = await resp.json();
    const created = Array.isArray(body) ? body[0] : body._list?.[0] || body;
    if (created?._userItemId) return created;
    // Fallback: fetch
    return await getCatalogCollection(ctx);
  } catch (e) {
    console.error('createCatalogCollection failed', e);
    return null;
  }
}

export async function listCatalogEntries(collection: CatalogCollection, ctx: PlatformContext): Promise<CatalogEntry[]> {
  if (!collection?._userItemId) return [];
  try {
    const res = await IafItemSvc.getRelatedItems(
      collection._userItemId,
      {},
      ctx,
      { page: { _pageSize: 1000 } }
    );
    const list = res?._list || [];
    return list.map((it: any) => ({
      _id: it._id,
      category: it.category || it._userAttributes?.category,
      typeName: it.typeName || it._userAttributes?.typeName,
      typeCode: it.typeCode || it._userAttributes?.typeCode,
    })).filter((e: CatalogEntry) => e.category && e.typeName && e.typeCode);
  } catch (e) {
    console.warn('listCatalogEntries failed', e);
    return [];
  }
}

export async function addCatalogEntry(collection: CatalogCollection, entry: CatalogEntry, ctx: PlatformContext): Promise<void> {
  if (!collection?._userItemId) throw new Error('Catalog collection not initialized');
  const row = { ...entry, name: `${entry.category}::${entry.typeCode}` };
  await IafItemSvc.createRelatedItemsBulk(collection._userItemId, [row], ctx);
}

export async function updateCatalogEntry(collection: CatalogCollection, entryId: string, entry: CatalogEntry, ctx: PlatformContext): Promise<void> {
  if (!collection?._userItemId) throw new Error('Catalog collection not initialized');
  const row = { _id: entryId, ...entry, name: `${entry.category}::${entry.typeCode}` };
  await IafItemSvc.updateRelatedItems(collection._userItemId, [row], ctx);
}

export async function deleteCatalogEntry(collection: CatalogCollection, entryId: string, ctx: PlatformContext): Promise<void> {
  if (!collection?._userItemId) throw new Error('Catalog collection not initialized');
  await IafItemSvc.deleteRelatedItems(collection._userItemId, [entryId], ctx);
}

export function deriveEntriesFromSeed(): CatalogEntry[] {
  const entries: CatalogEntry[] = [];
  try {
    const root = bimEquipment as Record<string, any[]>; // category -> array of objects with equipment
    Object.keys(root || {}).forEach((category) => {
      const arr = root[category];
      if (Array.isArray(arr)) {
        arr.forEach((it: any) => {
          const typeName: string = it?.equipment || it?.type || '';
          if (!typeName) return;
          const typeCode = generateTypeCode(typeName);
          entries.push({ category, typeName, typeCode });
        });
      }
    });
  } catch (e) {
    console.warn('deriveEntriesFromSeed failed', e);
  }
  // Remove duplicates by category+typeCode
  const uniq = new Map<string, CatalogEntry>();
  for (const e of entries) {
    const key = `${e.category.toLowerCase()}|${e.typeCode.toLowerCase()}`;
    if (!uniq.has(key)) uniq.set(key, e);
  }
  return Array.from(uniq.values());
}

export async function seedCatalogFromJson(collection: CatalogCollection, ctx: PlatformContext): Promise<number> {
  const seed = deriveEntriesFromSeed();
  if (!collection?._userItemId || seed.length === 0) return 0;
  const existing = await listCatalogEntries(collection, ctx);
  const existKeys = new Set(existing.map(e => `${e.category.toLowerCase()}|${e.typeCode.toLowerCase()}`));
  const toCreate = seed.filter(e => !existKeys.has(`${e.category.toLowerCase()}|${e.typeCode.toLowerCase()}`));
  if (toCreate.length === 0) return 0;
  const rows = toCreate.map(e => ({ ...e, name: `${e.category}::${e.typeCode}` }));
  await IafItemSvc.createRelatedItemsBulk(collection._userItemId, rows, ctx);
  return rows.length;
}
